import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CaqRequestService } from '../caq-request/caq-request.service';
import { LocalizacaoService } from '../localizacao/localizacao.service';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TipoSimulacao } from '../../enums/simulation-type.enum';
import { NumeroTotalDocentesDto } from '../../dtos/numero-total-docentes.dto';

interface QuantitativoDocentesResponse<T> {
    content: NumeroTotalDocentesDto;
}

@Injectable({ providedIn: 'root' })
export class QuantitativoDocentesService {
    private apiUrl = `${environment.apiUrl}/quantitativo-docentes`;

    constructor(
        private caqRequest: CaqRequestService,
        private localizacaoService: LocalizacaoService
    ) { }

    obterNacional(): Promise<NumeroTotalDocentesDto> {
        return firstValueFrom(
            this.caqRequest
                .get<QuantitativoDocentesResponse<NumeroTotalDocentesDto>>(`${this.apiUrl}/nacional`)
                .pipe(map(res => res.content))
        );
    }

    //Busca pela localização/tipo de simulação atual
    async obterPorLocalizacao(): Promise<NumeroTotalDocentesDto> {
        const url = this.construirUrlPorLocalizacao();
        return firstValueFrom(
            this.caqRequest
                .get<QuantitativoDocentesResponse<NumeroTotalDocentesDto>>(url)
                .pipe(map(res => res.content))
        );
    }

    obterRedeMunicipio(codMun: number | string): Promise<NumeroTotalDocentesDto> {
        return firstValueFrom(
            this.caqRequest
                .get<QuantitativoDocentesResponse<NumeroTotalDocentesDto>>(`${this.apiUrl}/rede/municipio/${codMun}`)
                .pipe(map(res => res.content))
        );
    }

    obterPlanoMunicipio(codMun: number | string): Promise<NumeroTotalDocentesDto> {
        return firstValueFrom(
            this.caqRequest
                .get<QuantitativoDocentesResponse<NumeroTotalDocentesDto>>(`${this.apiUrl}/plano/municipio/${codMun}`)
                .pipe(map(res => res.content))
        );
    }

    obterRedeEstado(codUf: number | string): Promise<NumeroTotalDocentesDto> {
        return firstValueFrom(
            this.caqRequest
                .get<QuantitativoDocentesResponse<NumeroTotalDocentesDto>>(`${this.apiUrl}/rede/estado/${codUf}`)
                .pipe(map(res => res.content))
        );
    }

    obterPlanoEstado(codUf: number | string): Observable<NumeroTotalDocentesDto> {
        return this.caqRequest
            .get<QuantitativoDocentesResponse<NumeroTotalDocentesDto>>(`${this.apiUrl}/plano/estado/${codUf}`)
            .pipe(map(res => res.content));
    }

    private construirUrlPorLocalizacao(): string {
        const localizacao = this.localizacaoService.obterLocalizacaoSelecionada();

        // Sem seleção -> nacional
        if (!localizacao) {
            return `${this.apiUrl}/nacional`;
        }

        const tipo = localizacao.tipoSimulacaoSelecionado;

        // Planejamento nacional
        if (tipo === TipoSimulacao.planejamentoNacional) {
            return `${this.apiUrl}/nacional`;
        }

        // Municipal
        if (localizacao.cidadeSelecionada) {
            const codMun = localizacao.cidadeSelecionada.value;

            if (tipo === TipoSimulacao.planejamento) {
                // plano municipal
                return `${this.apiUrl}/plano/municipio/${codMun}`;
            } else if (tipo === TipoSimulacao.planejamentoPorEsferaAdmMunicipal) {
                // rede municipal
                return `${this.apiUrl}/rede/municipio/${codMun}`;
            }
        }

        // Estadual
        if (localizacao.estadoSelecionado) {
            const codUf = localizacao.estadoSelecionado.value;

            if (tipo === TipoSimulacao.planejamento) {
                // plano estadual
                return `${this.apiUrl}/plano/estado/${codUf}`;
            } else if (tipo === TipoSimulacao.planejamentoPorEsferaAdmEstadual) {
                // rede estadual
                return `${this.apiUrl}/rede/estado/${codUf}`;
            }
        }

        throw new Error('Tipo de simulação ou localização inválida');
    }
}
